import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { streamEvent, type StreamEvent } from '@app/shared';
import { api } from './api';
import { qk } from './queryKeys';
import { isPageActive, subscribePageActivity } from './pageActivity';

/**
 * Operations SSE hook (§14). Opens an `EventSource` to `GET /api/…/stream` and, on
 * each event, INVALIDATES the affected TanStack Query so the run transcript,
 * approvals inbox and notification badge stay live without polling. The SSE payload
 * is only a routing hint — the query refetch reads canonical state (I1).
 *
 * Auth (fixes the log-leak finding): the stream is opened with a short-lived,
 * single-use `?ticket=` minted by `POST …/stream/ticket` — the 30-day session token
 * NEVER rides the URL. Because a ticket is single-use, we cannot rely on
 * `EventSource`'s built-in auto-reconnect (a reconnect would replay the spent
 * ticket); instead we manage the lifecycle here: mint a FRESH ticket for each
 * (re)connect and reopen on error with a small backoff. On reconnect we invalidate
 * everything (the server can't replay across a fresh connection — §14 resync).
 *
 * Idle pause (scale-to-zero): every open stream makes the server poll the DB every
 * 12s, so a dashboard tab left open overnight kept the Neon compute awake 24/7.
 * The stream is closed while the page is inactive (hidden, or untouched for 30 min —
 * see `pageActivity`), and reopened (with a resync) as soon as it is used again.
 */
export function useOperationsStream(workspaceId: string | null): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!workspaceId) return;

    let source: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    let connecting = false;
    let firstConnect = true;
    const isActive = isPageActive;

    const invalidateAll = (): void => {
      void queryClient.invalidateQueries({ queryKey: qk.runs(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: qk.approvals(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: qk.notifications(workspaceId) });
    };

    const handle = (raw: MessageEvent): void => {
      let parsed: StreamEvent;
      try {
        parsed = streamEvent.parse(JSON.parse(raw.data as string));
      } catch {
        return;
      }
      switch (parsed.kind) {
        case 'run_update':
          void queryClient.invalidateQueries({ queryKey: qk.runs(workspaceId) });
          void queryClient.invalidateQueries({ queryKey: qk.run(workspaceId, parsed.runId) });
          break;
        case 'approval_update':
          void queryClient.invalidateQueries({ queryKey: qk.approvals(workspaceId) });
          void queryClient.invalidateQueries({
            queryKey: qk.approval(workspaceId, parsed.approvalId),
          });
          break;
        case 'notification_update':
          void queryClient.invalidateQueries({ queryKey: qk.notifications(workspaceId) });
          break;
      }
    };

    const scheduleReconnect = (): void => {
      if (cancelled || reconnectTimer || !isActive()) return;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = undefined;
        void connect();
      }, 3000);
    };

    const connect = async (): Promise<void> => {
      if (cancelled || connecting || source || !isActive()) return;
      connecting = true;
      let ticket: string;
      try {
        ({ ticket } = await api.mintStreamTicket(workspaceId));
      } catch {
        connecting = false;
        scheduleReconnect();
        return;
      }
      connecting = false;
      if (cancelled || !isActive()) return;

      // A reconnect (not the first connect) missed events during the gap → resync.
      if (!firstConnect) invalidateAll();
      firstConnect = false;

      const es = new EventSource(api.streamUrl(workspaceId, ticket));
      source = es;
      es.addEventListener('run_update', handle);
      es.addEventListener('approval_update', handle);
      es.addEventListener('notification_update', handle);
      es.addEventListener('resync', invalidateAll);
      es.onerror = () => {
        // EventSource can't re-auth (single-use ticket), so close and remint.
        es.close();
        if (source === es) source = null;
        scheduleReconnect();
      };
    };

    const pause = (): void => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
      source?.close();
      source = null;
    };

    const unsubscribe = subscribePageActivity(() => {
      if (isActive()) void connect();
      else pause();
    });

    void connect();

    return () => {
      cancelled = true;
      unsubscribe();
      pause();
    };
  }, [workspaceId, queryClient]);
}
